/** Shared Rough Cut + Shot List + optional Merge footer UI.
 * Shared Rough Cut / Shot timeline / Shot list for storyboard_generation
 * orchestration (VISTOERA_STORYBOARD_UNIFIED_VIDEO_ORCHESTRATION_…_P0_V1).
 * Timeline / assembly helpers stay in videoProductionTimeline.ts +
 * videoProductionCanvas.ts — this file only owns the React surface.
 */

import { EllipsisOutlined, LoadingOutlined, PauseCircleFilled, PlayCircleFilled } from '@ant-design/icons'
import { Tooltip } from 'antd'
import CanvasDropdown from '../CanvasDropdown'
import { memo, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import {
  summarizeShotUnits,
  isFinalVideoArtifactStale,
  shotUnitsToTimelineShots,
  type FinalVideoArtifactSnapshot,
  type ShotUnit,
  type ShotUnitStatus,
} from '../videoProductionCanvas'
import {
  advanceClock,
  applyBrokenVideoOverrides,
  buildTimelineSegments,
  clampGlobalTimeSec,
  resolveMissingSegmentOverlayCopy,
  resolveSegmentAtTime,
  seekToShotStart,
  type TimelineSegment,
} from '../videoProductionTimeline'
import { videoProductionAssemblyOutputUrl } from '../../../../api'

export type ShotVideoOrchestrationPanelProps = {
  units: ShotUnit[]
  workspaceId: string
  canonicalTotalDurationSec?: number | null
  finalVideoArtifact: FinalVideoArtifactSnapshot | null
  assemblyRunStatus?: string | null
  assemblyErrorMessage?: string | null
  emptyCaption?: string
  /** compact = unified storyboard (narrow columns); legacy = old VP labels */
  density: 'compact' | 'legacy'
  /** thumb click: seek only (unified) vs open generator (legacy) */
  thumbClickMode: 'seek' | 'open_generator'
  onOpenShotGenerator: (shotId: string) => void
  onRunAssembly?: () => void
  showMergeFooter?: boolean
  className?: string
}

const LEGACY_STATUS_LABEL: Record<ShotUnitStatus, string> = {
  READY_TO_PREP: '待准备',
  READY_TO_GENERATE: '可生成',
  GENERATING: '生成中',
  GENERATED: '已生成',
  MISSING_VIDEO: '缺少视频',
  FAILED: '失败',
  FORMAT_MISMATCH: '规格不匹配',
}

const COMPACT_STATUS_LABEL: Record<ShotUnitStatus, string> = {
  READY_TO_PREP: '待生成',
  READY_TO_GENERATE: '待生成',
  GENERATING: '生成中',
  GENERATED: '已生成',
  MISSING_VIDEO: '缺视频',
  FAILED: '失败',
  FORMAT_MISMATCH: '规格失效',
}

const STATUS_DOT_CLASS: Record<ShotUnitStatus, string> = {
  READY_TO_PREP: 'pcv-vp-dot--pending',
  READY_TO_GENERATE: 'pcv-vp-dot--pending',
  GENERATING: 'pcv-vp-dot--generating',
  GENERATED: 'pcv-vp-dot--ready',
  MISSING_VIDEO: 'pcv-vp-dot--missing',
  FAILED: 'pcv-vp-dot--missing',
  FORMAT_MISMATCH: 'pcv-vp-dot--missing',
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return '0秒'
  if (seconds < 60) return `${Math.round(seconds)}秒`
  const minutes = Math.floor(seconds / 60)
  const rem = Math.round(seconds % 60)
  return rem > 0 ? `${minutes}分${rem}秒` : `${minutes}分钟`
}

function formatClock(seconds: number): string {
  const s = Math.max(0, seconds)
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

/** Compact range: `mm:ss–ss` of end (e.g. `00:00–01`). Legacy keeps full clocks. */
function formatClockRange(startSec: number, endSec: number, compact: boolean): string {
  if (!compact) return `${formatClock(startSec)}–${formatClock(endSec)}`
  return `${formatClock(startSec)}–${String(Math.floor(Math.max(0, endSec) % 60)).padStart(2, '0')}`
}

function shotActionLabel(u: ShotUnit): string {
  if (u.status === 'GENERATING') return '生成中'
  if (u.status === 'GENERATED' || (Boolean(u.thumbnailUrl) && u.status !== 'MISSING_VIDEO')) return '编辑视频'
  return '生成视频'
}

function ShotVideoOrchestrationPanelImpl({
  units,
  workspaceId,
  canonicalTotalDurationSec,
  finalVideoArtifact,
  assemblyRunStatus = null,
  assemblyErrorMessage = null,
  emptyCaption,
  density,
  thumbClickMode,
  onOpenShotGenerator,
  onRunAssembly,
  showMergeFooter = false,
  className,
}: ShotVideoOrchestrationPanelProps) {
  const isCompact = density === 'compact'
  const statusLabel = isCompact ? COMPACT_STATUS_LABEL : LEGACY_STATUS_LABEL
  const summary = useMemo(() => summarizeShotUnits(units), [units])
  const isAssembling = assemblyRunStatus === 'ASSEMBLING' || assemblyRunStatus === 'SUBMITTING'

  // P0-1: Shots whose real `videoUrl` failed to decode in THIS browser
  // session — transient/local-only (never persisted).
  const [brokenShotIds, setBrokenShotIds] = useState<Set<string>>(new Set())
  const rawSegments: TimelineSegment[] = useMemo(() => buildTimelineSegments(shotUnitsToTimelineShots(units)), [units])
  const segments: TimelineSegment[] = useMemo(() => applyBrokenVideoOverrides(rawSegments, brokenShotIds), [rawSegments, brokenShotIds])

  const lastVideoJobIdByShotRef = useRef<Record<string, string | null>>({})
  useEffect(() => {
    const prevMap = lastVideoJobIdByShotRef.current
    const nextMap: Record<string, string | null> = {}
    const toClear: string[] = []
    for (const u of units) {
      nextMap[u.shotId] = u.videoJobId
      if (prevMap[u.shotId] !== undefined && prevMap[u.shotId] !== u.videoJobId) toClear.push(u.shotId)
    }
    lastVideoJobIdByShotRef.current = nextMap
    if (toClear.length > 0) {
      setBrokenShotIds((prev) => {
        if (!toClear.some((id) => prev.has(id))) return prev
        const next = new Set(prev)
        for (const id of toClear) next.delete(id)
        return next
      })
    }
  }, [units])

  const totalDurationSec = useMemo(() => {
    if (typeof canonicalTotalDurationSec === 'number' && canonicalTotalDurationSec > 0) return canonicalTotalDurationSec
    return segments.length ? segments[segments.length - 1].endSec : 0
  }, [segments, canonicalTotalDurationSec])

  const stale = useMemo(
    () => (finalVideoArtifact ? isFinalVideoArtifactStale(finalVideoArtifact, units) : false),
    [finalVideoArtifact, units],
  )

  const showFinalPlayer = Boolean(finalVideoArtifact && !stale && workspaceId)
  const finalVideoRef = useRef<HTMLVideoElement | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [globalTimeSec, setGlobalTimeSec] = useState(0)
  const globalTimeRef = useRef(0)
  const rafIdRef = useRef<number | null>(null)
  const lastTsRef = useRef<number | null>(null)
  const lastUiSyncRef = useRef(0)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const activeShotIdRef = useRef<string | null>(null)
  const thumbStripRef = useRef<HTMLDivElement | null>(null)
  const thumbButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  const resolved = useMemo(() => resolveSegmentAtTime(segments, globalTimeSec), [segments, globalTimeSec])
  const activeSegment = resolved?.segment ?? null

  const stopClock = useCallback(() => {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
    lastTsRef.current = null
  }, [])

  const tick = useCallback(
    (ts: number) => {
      if (lastTsRef.current == null) lastTsRef.current = ts
      const deltaSec = Math.min(0.25, Math.max(0, (ts - lastTsRef.current) / 1000))
      lastTsRef.current = ts
      const { nextGlobalTimeSec, ended } = advanceClock(globalTimeRef.current, deltaSec, totalDurationSec)
      globalTimeRef.current = nextGlobalTimeSec
      if (ts - lastUiSyncRef.current > 80 || ended) {
        lastUiSyncRef.current = ts
        setGlobalTimeSec(nextGlobalTimeSec)
      }
      if (ended) {
        setIsPlaying(false)
        stopClock()
        return
      }
      rafIdRef.current = requestAnimationFrame(tick)
    },
    [stopClock, totalDurationSec],
  )

  useEffect(() => {
    if (!isPlaying) {
      stopClock()
      videoRef.current?.pause()
      return
    }
    rafIdRef.current = requestAnimationFrame(tick)
    return stopClock
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying])

  useEffect(() => {
    const el = videoRef.current
    if (!el || !activeSegment || activeSegment.kind !== 'video') {
      activeShotIdRef.current = activeSegment?.shotId ?? null
      return
    }
    const localTime = resolved?.localTimeSec ?? 0
    if (activeShotIdRef.current !== activeSegment.shotId) {
      activeShotIdRef.current = activeSegment.shotId
      try {
        el.currentTime = localTime
      } catch {
        /* ignore — element may not be ready yet */
      }
    }
    if (isPlaying) {
      el.play().catch(() => {})
    } else {
      el.pause()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSegment?.shotId, activeSegment?.kind, isPlaying])

  const activeShotId = activeSegment?.shotId ?? null
  const activeUnit = units.find((u) => u.shotId === activeShotId) ?? null
  const activeShotIndexLabel = activeUnit ? units.indexOf(activeUnit) + 1 : null

  // Auto-scroll thumb strip only when active Shot changes (not every tick).
  useEffect(() => {
    if (!activeShotId) return
    const btn = thumbButtonRefs.current.get(activeShotId)
    if (!btn || typeof btn.scrollIntoView !== 'function') return
    btn.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }, [activeShotId])

  const placeholderOverlay = useMemo(() => {
    if (!activeSegment || activeSegment.kind !== 'placeholder') return null
    return resolveMissingSegmentOverlayCopy({
      reason: activeSegment.posterFallbackReason,
      shotIndexLabel: activeShotIndexLabel,
      durationSec: activeSegment.durationSec,
    })
  }, [activeSegment, activeShotIndexLabel])

  const togglePlay = () => {
    if (units.length === 0) return
    if (!isPlaying && globalTimeRef.current >= totalDurationSec && totalDurationSec > 0) {
      globalTimeRef.current = 0
      setGlobalTimeSec(0)
      setBrokenShotIds(new Set())
    }
    setIsPlaying((v) => !v)
  }

  const seekTo = useCallback(
    (nextSec: number) => {
      const clamped = clampGlobalTimeSec(nextSec, totalDurationSec)
      globalTimeRef.current = clamped
      setGlobalTimeSec(clamped)
    },
    [totalDurationSec],
  )

  const seekToShot = useCallback(
    (shotId: string) => {
      setBrokenShotIds((prev) => {
        if (!prev.has(shotId)) return prev
        const next = new Set(prev)
        next.delete(shotId)
        return next
      })
      const start = seekToShotStart(segments, shotId)
      if (start != null) seekTo(start)
    },
    [segments, seekTo],
  )

  const handleActiveVideoError = useCallback(() => {
    const shotId = activeSegment?.shotId
    if (!shotId) return
    setBrokenShotIds((prev) => (prev.has(shotId) ? prev : new Set(prev).add(shotId)))
  }, [activeSegment])

  const handleThumbClick = useCallback(
    (e: ReactMouseEvent, shotId: string) => {
      e.stopPropagation()
      seekToShot(shotId)
      if (thumbClickMode === 'open_generator') onOpenShotGenerator(shotId)
    },
    [seekToShot, thumbClickMode, onOpenShotGenerator],
  )

  const handleRowClick = useCallback(
    (e: ReactMouseEvent, shotId: string) => {
      e.stopPropagation()
      seekToShot(shotId)
      // Legacy: row click also opens generator (pre-extraction behavior).
      // Compact: seek only — action button opens generator.
      if (!isCompact) onOpenShotGenerator(shotId)
    },
    [seekToShot, isCompact, onOpenShotGenerator],
  )

  const handleOpenGenerator = useCallback(
    (e: ReactMouseEvent, shotId: string) => {
      e.stopPropagation()
      seekToShot(shotId)
      onOpenShotGenerator(shotId)
    },
    [seekToShot, onOpenShotGenerator],
  )

  const canRunAssembly = summary.allRequiredShotsReady && !isAssembling
  const handleRunAssembly = () => {
    if (!canRunAssembly || !onRunAssembly) return
    onRunAssembly()
  }

  const emptyText = emptyCaption || '暂无分镜内容'

  return (
    <div
      className={`pcv-shot-video-orchestration${className ? ` ${className}` : ''}`}
      data-testid="storyboard-shot-video-orchestration"
      data-density={density}
    >
      {/* Rough Cut / Final Result Player */}
      <div className="pcv-vp-player nodrag" data-testid="video-production-player" onClick={(e) => e.stopPropagation()}>
        {showFinalPlayer ? (
          <>
            <div className="pcv-vp-player-stage">
              <video
                ref={finalVideoRef}
                key={`final-${finalVideoArtifact!.assemblyId}`}
                src={videoProductionAssemblyOutputUrl(workspaceId, finalVideoArtifact!.assemblyId)}
                className="pcv-vp-player-video"
                controls
                playsInline
                muted
                data-testid="video-production-final-player"
              />
            </div>
            <div className="pcv-vp-player-caption">
              <span>✓ 最终视频 · {formatDuration(finalVideoArtifact!.durationSeconds)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="pcv-vp-player-stage">
              {activeSegment?.kind === 'video' ? (
                <video
                  ref={videoRef}
                  key={activeSegment.shotId}
                  src={activeSegment.videoUrl || undefined}
                  className="pcv-vp-player-video"
                  playsInline
                  muted
                  onEnded={() => {
                    /* Advanced past this segment by the RAF clock itself. */
                  }}
                  onError={handleActiveVideoError}
                  data-testid="video-production-roughcut-player"
                />
              ) : (
                <div
                  className="pcv-vp-player-placeholder-dark"
                  data-testid="video-production-placeholder"
                  data-placeholder-reason={activeSegment?.posterFallbackReason ?? 'NONE'}
                  data-active-shot-id={activeSegment?.shotId ?? ''}
                >
                  {activeSegment?.posterFallbackReason === 'GENERATING' ? <LoadingOutlined spin /> : null}
                  {units.length === 0 ? (
                    <span>{emptyText}</span>
                  ) : placeholderOverlay ? (
                    <div className="pcv-vp-missing-overlay" data-testid="video-production-missing-overlay">
                      <span className="pcv-vp-missing-overlay-title">{placeholderOverlay.title}</span>
                      <span className="pcv-vp-missing-overlay-subtitle">{placeholderOverlay.subtitle}</span>
                      <span className="pcv-vp-missing-overlay-hint">{placeholderOverlay.hint}</span>
                    </div>
                  ) : (
                    <span>缺少视频</span>
                  )}
                </div>
              )}
              <button type="button" className="pcv-vp-player-playbtn" onClick={togglePlay} disabled={units.length === 0} aria-label={isPlaying ? '暂停' : '播放'}>
                {isPlaying ? <PauseCircleFilled /> : <PlayCircleFilled />}
              </button>
            </div>
            <div className="pcv-vp-player-caption">
              {activeUnit ? (
                <span>
                  #{activeShotIndexLabel}{' '}
                  {formatClockRange(activeSegment!.startSec, activeSegment!.endSec, isCompact)} {activeUnit.description}
                </span>
              ) : (
                <span className="pcv-vp-player-caption-empty">{emptyText}</span>
              )}
            </div>
            <div className="pcv-vp-player-scrubber-row">
              <span className="pcv-vp-player-time">{formatClock(globalTimeSec)}</span>
              <input
                type="range"
                className="pcv-vp-player-scrubber nodrag"
                min={0}
                max={Math.max(0.001, totalDurationSec)}
                step={0.05}
                value={Math.min(globalTimeSec, totalDurationSec)}
                disabled={units.length === 0}
                onChange={(e) => seekTo(Number(e.target.value))}
              />
              <span className="pcv-vp-player-time">{formatClock(totalDurationSec)}</span>
            </div>
          </>
        )}
      </div>

      {/* Shot Thumbnail Timeline */}
      <div className="pcv-vp-thumb-strip-wrap nodrag" data-testid="video-production-thumb-strip">
        <span className="pcv-vp-thumb-strip-clock">{formatClock(0)}</span>
        <div className="pcv-vp-thumb-strip" ref={thumbStripRef}>
          {units.map((u, idx) => (
            <Tooltip key={u.shotId} title={`#${idx + 1} ${statusLabel[u.status]}`} mouseEnterDelay={0.3}>
              <button
                type="button"
                ref={(el) => {
                  if (el) thumbButtonRefs.current.set(u.shotId, el)
                  else thumbButtonRefs.current.delete(u.shotId)
                }}
                className={`pcv-vp-thumb${activeShotId === u.shotId ? ' is-active' : ''}`}
                data-testid="video-production-thumb-item"
                data-shot-id={u.shotId}
                data-shot-status={u.status}
                data-active={activeShotId === u.shotId ? 'true' : 'false'}
                onClick={(e) => handleThumbClick(e, u.shotId)}
              >
                {u.thumbnailUrl ? <img src={u.thumbnailUrl} alt="" /> : <span className="pcv-vp-thumb-index">#{idx + 1}</span>}
                <span className={`pcv-vp-dot ${STATUS_DOT_CLASS[u.status]}`} />
                {!u.thumbnailUrl && u.status !== 'GENERATED' ? (
                  <span className="pcv-vp-thumb-missing-label">未生成 {formatDuration(u.durationSec)}</span>
                ) : null}
              </button>
            </Tooltip>
          ))}
          {units.length === 0 ? <span className="pcv-vp-thumb-strip-empty">{emptyText}</span> : null}
        </div>
        <span className="pcv-vp-thumb-strip-clock">{formatClock(totalDurationSec)}</span>
      </div>

      {/* Shot List */}
      <div
        className={`pcv-vp-shot-list nodrag${isCompact ? ' pcv-vp-shot-list--compact' : ''}`}
        data-testid="video-production-shot-list"
      >
        {units.length === 0 ? (
          <div className="pcv-storyboard-empty pcv-storyboard-empty--compact">
            <span>{emptyText}</span>
          </div>
        ) : (
          <>
            <div className="pcv-vp-shot-list-header" data-testid="video-production-shot-list-header">
              {isCompact ? (
                <>
                  <span>#</span>
                  <span>图</span>
                  <span>时间</span>
                  <span>时长</span>
                  <span>状态</span>
                  <span>内容</span>
                  <span>···</span>
                </>
              ) : (
                <>
                  <span>#</span>
                  <span>缩略图</span>
                  <span>时间区间</span>
                  <span>时长</span>
                  <span>状态</span>
                  <span>内容描述</span>
                  <span>操作</span>
                </>
              )}
            </div>
            {units.map((u, idx) => (
              <div
                key={u.shotId}
                className={`pcv-vp-shot-row${activeShotId === u.shotId ? ' is-active' : ''}`}
                data-testid="video-production-shot-row"
                data-shot-id={u.shotId}
                onClick={(e) => handleRowClick(e, u.shotId)}
              >
                <span className="pcv-vp-shot-index">#{idx + 1}</span>
                <span className="pcv-vp-shot-thumb">
                  {u.thumbnailUrl ? <img src={u.thumbnailUrl} alt="" /> : <span className="pcv-vp-thumb-index">—</span>}
                </span>
                <span className="pcv-vp-shot-tc">{formatClockRange(u.timelineStartSec, u.timelineEndSec, isCompact)}</span>
                <span className="pcv-vp-shot-duration">{formatDuration(u.durationSec)}</span>
                <span className={`pcv-vp-shot-status pcv-vp-shot-status--${u.status.toLowerCase()}`}>{statusLabel[u.status]}</span>
                <span className="pcv-vp-shot-desc" title={u.description || u.title || undefined}>
                  {u.description || u.title || '—'}
                </span>
                {isCompact ? (
                  <button
                    type="button"
                    className="pcv-vp-shot-action-btn nodrag nopan"
                    data-testid="storyboard-orchestration-shot-action"
                    disabled={u.status === 'GENERATING'}
                    onClick={(e) => handleOpenGenerator(e, u.shotId)}
                  >
                    {shotActionLabel(u)}
                  </button>
                ) : (
                  <CanvasDropdown
                    trigger={['click']}
                    menu={{
                      items: [
                        {
                          key: 'advanced-edit',
                          label: '高级编辑',
                          onClick: () => {
                            seekToShot(u.shotId)
                            onOpenShotGenerator(u.shotId)
                          },
                        },
                      ],
                    }}
                  >
                    <button
                      type="button"
                      className="pcv-media-hover-btn nodrag nopan"
                      aria-label="镜头操作"
                      data-testid="video-production-shot-row-menu"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EllipsisOutlined />
                    </button>
                  </CanvasDropdown>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Optional Merge Settings Footer */}
      {showMergeFooter ? (
        <div className="pcv-vp-assembly-footer nodrag" data-testid="video-production-merge-settings" onClick={(e) => e.stopPropagation()}>
          <div className="pcv-vp-assembly-footer-title">合并设置</div>
          <div className="pcv-vp-assembly-footer-row">
            <div className="pcv-vp-merge-field">
              <span className="pcv-vp-merge-field-label">Provider</span>
              <span className="pcv-gen-footer-provider-label" data-testid="video-merge-provider-label">
                标准合成（FFmpeg）
              </span>
            </div>
            <div className="pcv-vp-merge-field">
              <span className="pcv-vp-merge-field-label">Skill</span>
              <span className="pcv-gen-footer-skill-label" data-testid="video-merge-skill-label">
                标准视频拼合
              </span>
            </div>
            <button
              type="button"
              className="pcv-gen-footer-generate-btn"
              data-testid="video-production-run-assembly-btn"
              disabled={!canRunAssembly}
              onClick={handleRunAssembly}
            >
              {isAssembling ? <LoadingOutlined spin /> : null} {isAssembling ? '合并中…' : '合并生成视频'}
            </button>
          </div>
          <div className="pcv-gen-footer-hint" data-testid="video-production-assembly-hint">
            {summary.total === 0
              ? '连接分镜后即可合并生成视频。'
              : summary.allRequiredShotsReady
                ? `已生成的${summary.total}个镜头即将合并为完整视频（${formatDuration(totalDurationSec)}）。`
                : `已生成${summary.generatedCount}/${summary.total}个镜头。若有尚未生成的镜头，将无法合并完整视频。`}
          </div>
          {assemblyRunStatus === 'FAILED' && assemblyErrorMessage ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{assemblyErrorMessage}</div>
          ) : null}
          {finalVideoArtifact ? (
            <div className={`pcv-vp-final-result${stale ? ' pcv-vp-final-result--stale' : ''}`} data-testid="video-production-final-result">
              <span>
                {stale ? '⚠ 合并结果已过期（镜头已更新）' : '✓ 最终视频已合并'} · {formatDuration(finalVideoArtifact.durationSeconds)}
              </span>
              {workspaceId ? (
                <a
                  className="pcv-vp-final-result-link"
                  href={videoProductionAssemblyOutputUrl(workspaceId, finalVideoArtifact.assemblyId)}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="video-production-final-result-link"
                >
                  查看最终视频
                </a>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export default memo(ShotVideoOrchestrationPanelImpl)
